import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { walletAdminAPI } from '../../services/api';

/**
 * Admin: FoodWinc Wallet settings — enable the wallet, set recharge limits, and the
 * assured cashback given on a wallet-paid order (Phase 2, credited on delivery).
 */
function WalletSettings() {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await walletAdminAPI.getSettings();
      const d = r.data?.data || {};
      setCfg({
        enabled: !!d.enabled,
        minRecharge: d.minRecharge ?? 100,
        maxRecharge: d.maxRecharge ?? 10000,
        cashbackAmount: d.cashbackAmount ?? 30,
        minOrderForCashback: d.minOrderForCashback ?? 100,
      });
    } catch (e) { setMsg('Failed to load settings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await walletAdminAPI.updateSettings(cfg);
      setMsg('Saved.');
      load();
    } catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!cfg) return (<div><Navbar /><div style={{ padding: 24 }}>Loading…</div></div>);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
  const input = { width: '100%', padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4 };
  const numField = (key, lbl, hint) => (
    <div>
      <label style={label}>{lbl}</label>
      <input type="number" min="0" style={input} value={cfg[key]} onChange={(e) => set(key, e.target.value)} />
      {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{hint}</div>}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 4px' }}>👛 FoodWinc Wallet</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 18px' }}>
          Customers can recharge a prepaid wallet and pay for orders with it. Paying via wallet earns an assured cashback (credited on delivery). All amounts are in whole rupees.
        </p>

        <div style={card}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#334155' }}>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Enable FoodWinc Wallet (shows the wallet in the app)
          </label>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Recharge limits</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {numField('minRecharge', 'Minimum recharge (₹)', 'Smallest amount a user can add.')}
            {numField('maxRecharge', 'Maximum recharge (₹)', 'Largest single recharge.')}
          </div>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Cashback on wallet payment</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {numField('cashbackAmount', 'Assured cashback (₹)', 'Credited to the wallet when an order is paid by wallet.')}
            {numField('minOrderForCashback', 'Min order value to earn cashback (₹)', 'Order total must be at least this to qualify.')}
          </div>
          <div style={{ fontSize: 12, color: '#b45309', marginTop: 10 }}>
            Note: pay-with-wallet + cashback is Phase 2 — these values are stored now so they’re ready when it ships.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={save} disabled={saving} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {msg && <span style={{ fontSize: 14, color: msg === 'Saved.' ? '#16a34a' : '#dc2626' }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

export default WalletSettings;
